/**
 * Lodestar — per-route SEO helper.
 *
 * Wraps react-helmet-async so each public page can declare its own
 * `<title>`, meta description, canonical, and per-route Open Graph
 * overrides. Modern Googlebot runs JS + reads the rendered <head>, so
 * even though our static index.html ships a single generic title, by
 * the time the crawler indexes the route it sees the page-specific tags
 * injected here.
 *
 * Why not just react-helmet (without -async): -async is React 18-safe
 * (no `findDOMNode` deprecation, no Strict-Mode warnings) and is the
 * maintained fork.
 *
 * Defaults mirror what's in public/index.html so a page that omits a
 * field never accidentally blanks out a critical tag.
 */
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Lodestar";
const SITE_URL = "https://lodestarastrology.com";
const DEFAULT_DESC =
    "Lodestar — your personal cosmic life map. Decode your patterns, planetary influences, timing, and direction with Swiss Ephemeris + KP astrology.";
const DEFAULT_IMAGE = `${SITE_URL}/og-cover.jpg`;

/**
 * Render per-route SEO tags.
 *
 * @param {object} props
 * @param {string} props.title       — Page title (gets " · Lodestar" suffix
 *                                     unless `noSuffix` is true).
 * @param {string} props.description — Meta description (max 160 chars).
 * @param {string} [props.path]      — Canonical path on lodestarastrology.com
 *                                     (without origin). Defaults to current
 *                                     pathname read at mount time.
 * @param {string} [props.image]     — Override OG image (absolute URL).
 * @param {boolean} [props.noSuffix] — Skip the " · Lodestar" suffix (for the
 *                                     landing page where the brand is the title).
 * @param {boolean} [props.noindex]  — Add robots:noindex for non-public pages
 *                                     (dashboard, account, admin).
 */
export default function SEO({
    title,
    description = DEFAULT_DESC,
    path,
    image = DEFAULT_IMAGE,
    noSuffix = false,
    noindex = false,
}) {
    const fullTitle = title
        ? noSuffix
            ? title
            : `${title} · ${SITE_NAME}`
        : `${SITE_NAME} — Your Personal Cosmic Life Map`;
    const canonical = path
        ? `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`
        : typeof window !== "undefined"
          ? `${SITE_URL}${window.location.pathname}`
          : SITE_URL;
    const desc = (description || DEFAULT_DESC).slice(0, 280);
    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <link rel="canonical" href={canonical} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}

            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content="website" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={image} />
        </Helmet>
    );
}
